import type { LoggerEvent } from '@/types/logger.types'
import { loadTurnstile } from '@/utils/load-turnstile'

import { AdConsoleLogger } from './utils/ad-console-logger'

const adConsoleLogger = new AdConsoleLogger('detector')

export type BotStatus = 'human' | 'bot' | 'unknown'

// Turnstile error families 300xxx and 600xxx mean "challenge failed": the visitor looked automated.
// Every other code (network, unsupported browser, sitekey/hostname misconfiguration) says nothing about the visitor.
const isChallengeFailure = (errorCode: string) => /^[36]/.test(errorCode)

export async function detectBot(siteKey: string, logEvent: (event: LoggerEvent) => void): Promise<BotStatus> {
    const log = (status: BotStatus, detail: string) => {
        adConsoleLogger.debug('bot status', { status, detail })
        logEvent({ event: 'custom_event', eventName: 'ad_bot_detect_attempt', dimension1: status, dimension2: detail })
    }

    const turnstile = await loadTurnstile()
    if (!turnstile) {
        log('unknown', 'script_error')
        return 'unknown'
    }

    const container = document.createElement('div')
    document.body.appendChild(container)
    const { promise, resolve } = Promise.withResolvers<[BotStatus, string]>()
    try {
        turnstile.render(container, {
            sitekey: siteKey,
            action: 'ads',
            retry: 'never',
            callback: () => resolve(['human', '']),
            'error-callback': (errorCode: string) =>
                resolve([isChallengeFailure(errorCode) ? 'bot' : 'unknown', errorCode]),
            'unsupported-callback': () => resolve(['unknown', 'unsupported']),
        })
    } catch (error) {
        adConsoleLogger.error('Turnstile render failed', error)
        resolve(['unknown', 'render_error'])
    }

    const [status, detail] = await promise
    turnstile.remove(container)
    container.remove()
    log(status, detail)
    return status
}
